/**
 * Recipe Approval Workflow Demo Page
 * Demonstrates how to use the RecipeApprovalWorkflow modal component
 */

import React, { useState } from 'react';
import { Card, Button, Input, Typography, Space, Row, Col, Divider } from 'antd';
import { GitBranch, Eye } from 'lucide-react';
import RecipeApprovalWorkflow from '../../components/RecipeApprovalWorkflow';
import { useMbmrAlias } from '@/hooks/useAppSetting';

const { Title, Text, Paragraph } = Typography;

export default function RecipeApprovalDemo() {
  const [modalVisible, setModalVisible] = useState(false);
  const [recipeId, setRecipeId] = useState('1187');
  const mbmrAlias = useMbmrAlias();

  const handleViewWorkflow = () => {
    if (!recipeId) {
      alert(`Please enter a ${mbmrAlias} ID`);
      return;
    }
    setModalVisible(true);
  };

  // Example recipes for quick testing
  const exampleRecipes = [
    { id: '1187', name: `Demo ${mbmrAlias}`, product: 'ATRCA' },
    { id: '1166', name: 'Demo', product: 'ATRCA' },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <Card className="mb-6 border-t-4 border-blue-600">
        <div className="flex items-start gap-4">
          <GitBranch size={48} className="text-blue-600 mt-1" />
          <div>
            <Title level={2} className="mb-1">{mbmrAlias} Approval Workflow Viewer</Title>
            <Text type="secondary">
              View the complete approval history and workflow stages for any {mbmrAlias}
            </Text>
          </div>
        </div>
      </Card>

      {/* Main Content */}
      <Row gutter={[24, 24]}>
        {/* Left Column - Input */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <div className="flex items-center gap-2">
                <Eye size={20} />
                <span>View Workflow</span>
              </div>
            }
            className="h-full"
          >
            <Space direction="vertical" className="w-full" size="large">
              <div>
                <Text strong className="block mb-2">Enter {mbmrAlias} ID</Text>
                <Input
                  size="large"
                  placeholder="e.g., 1187"
                  value={recipeId}
                  onChange={(e) => setRecipeId(e.target.value)}
                  onPressEnter={handleViewWorkflow}
                />
              </div>

              <Button
                type="primary"
                size="large"
                icon={<GitBranch size={18} />}
                onClick={handleViewWorkflow}
                block
                className="h-12"
              >
                View Approval Workflow
              </Button>

              <Divider />

              <div>
                <Text strong className="block mb-3">Quick Access - Example {mbmrAlias}s</Text>
                <Space direction="vertical" className="w-full">
                  {exampleRecipes.map((recipe) => (
                    <Button
                      key={recipe.id}
                      onClick={() => {
                        setRecipeId(recipe.id);
                        setTimeout(() => setModalVisible(true), 100);
                      }}
                      block
                      className="text-left h-auto py-3"
                    >
                      <div>
                        <Text strong>{recipe.name}</Text>
                        <br />
                        <Text type="secondary" className="text-xs">
                          ID: {recipe.id} | Product: {recipe.product}
                        </Text>
                      </div>
                    </Button>
                  ))}
                </Space>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Right Column - Information */}
        <Col xs={24} lg={12}>
          <Card title="About This Feature" className="h-full">
            <Space direction="vertical" size="large" className="w-full">
              <div>
                <Title level={5}>{mbmrAlias} Approval Workflow</Title>
                <Paragraph>
                  This feature displays a pharmaceutical industry-standard vertical
                  timeline showing all approval stages for a specific {mbmrAlias}. Each
                  stage includes:
                </Paragraph>
                <ul className="pl-5">
                  <li>✅ {mbmrAlias} ID, Name, and Product</li>
                  <li>✅ Approval Status (Color-coded)</li>
                  <li>✅ Date and Time of Approval</li>
                  <li>✅ User who performed the approval</li>
                  <li>✅ Approval ID for tracking</li>
                  <li>✅ 21 CFR Part 11 Compliant Audit Trail</li>
                </ul>
              </div>

              <Divider />

              <div>
                <Title level={5}>How to Use</Title>
                <ol className="pl-5">
                  <li>Enter a {mbmrAlias} ID in the input field</li>
                  <li>Click "View Approval Workflow" button</li>
                  <li>The modal will display the complete approval history</li>
                  <li>Scroll through the vertical timeline to see all stages</li>
                </ol>
              </div>

              <Divider />

              <div>
                <Title level={5}>Pharma-Standard Features</Title>
                <ul className="pl-5">
                  <li>✅ Professional gradient header design</li>
                  <li>✅ Color-coded status indicators</li>
                  <li>✅ Stage-by-stage vertical timeline</li>
                  <li>✅ Complete audit trail information</li>
                  <li>✅ Real-time data from backend API</li>
                  <li>✅ Responsive and accessible</li>
                  <li>✅ Empty and loading states</li>
                  <li>✅ Latest stage highlighting</li>
                  <li>✅ Summary footer with statistics</li>
                </ul>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Recipe Approval Workflow Modal */}
      <RecipeApprovalWorkflow
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        recipeId={recipeId}
      />
    </div>
  );
}
